import React, { useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import ClickableIcon from '~components/ClickableIcon';
import Icon, { faChevronDown, faChevronUp, faEye, faEyeSlash } from '~components/icon';

import Pill from '../pill/Pill';

export interface BreakdownItem {
  label: string;
  value: string;
}

interface ComponentProps {
  title: string;
  value?: string | number | null;
  icon?: IconProp;
  iconColor?: string;
  pill?: {
    label?: string;
    color?: string;
    icon?: React.ReactNode;
  };
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  breakdown?: BreakdownItem[];
  dataCy?: string;
}

const sanitizeId = (str: string) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const Statistic: React.FC<ComponentProps> = ({
  title,
  pill,
  collapsed,
  value,
  icon,
  iconColor,
  onToggleCollapse,
  breakdown,
  dataCy,
}) => {
  const safeId = sanitizeId(title);
  const [expanded, setExpanded] = useState(false);
  const hasBreakdown = breakdown && breakdown.length > 0;

  return (
    <div className="statistic" data-cy={dataCy}>
      <div className="statistic-header">
        <h3 className="statistic-title">{title}</h3>
        <ClickableIcon
          id={`hide-${safeId}`}
          icon={collapsed ? faEye : faEyeSlash}
          size="x-small"
          shape="round"
          toolTip={collapsed ? 'Show' : 'Hide'}
          onClick={onToggleCollapse}
          color="grey"
          dataCy="statistic-hide"
        />
      </div>
      <div className="statistic-content">
        {!collapsed && <div className="statistic-value">{value}</div>}
        {!collapsed && pill && (
          <Pill color={pill?.color ?? '#000000'} text={pill?.label} icon={pill?.icon} className="statistic-pill" />
        )}
      </div>
      {icon && (
        <div className="statistic-icon">
          <Icon icon={icon} style={iconColor ? { color: iconColor } : undefined} />
        </div>
      )}
      {!collapsed && hasBreakdown && (
        <div className="statistic-breakdown-toggle">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${title} breakdown` : `Expand ${title} breakdown`}
            data-cy={dataCy ? `${dataCy}-expand-btn` : undefined}
          >
            <Icon icon={expanded ? faChevronUp : faChevronDown} />
          </button>
        </div>
      )}
      {!collapsed && expanded && hasBreakdown && (
        <div className="statistic-breakdown">
          {breakdown.map((item) => (
            <div key={item.label} className="statistic-breakdown-item">
              <span className="statistic-breakdown-label">{item.label}</span>
              <span className="statistic-breakdown-value">{item.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Statistic;
